/**
 * Template Build Status Hook
 *
 * Polling hook for template build status.
 * Automatically polls until build is ready for review, completed, or failed.
 * Includes timeout protection (5 minutes max).
 */

import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  isReadyForReview,
  toBuildStatus,
  useGetTemplateBuildStatusQuery,
} from '../store/api';
import {
  setBuildStatus,
  setCreatedTemplateId,
  setDocumentSummary,
  setDocumentTitle,
  setDraftTemplate,
  setError,
  setNeedsStatusRefetch,
  setProgressMessage,
  setSourceTemplateId,
} from '../store/chatSlice';
import {
  selectBuildStatus,
  selectDocumentSummary,
  selectDocumentTitle,
  selectDraftTemplate,
  selectNeedsStatusRefetch,
  selectProgressMessage,
} from '../store/selectors';
import { toDraftTemplate } from '../types';

// Polling interval in milliseconds
const POLL_INTERVAL = 2000;
// Maximum polling duration (5 minutes)
const MAX_POLLING_DURATION_MS = 5 * 60 * 1000;

/**
 * Hook for polling template build status
 *
 * @param taskId - The template build task ID
 * @returns Build status, progress message, draft template, and polling state
 */
export const useTemplateBuildStatus = (taskId: string | null) => {
  const dispatch = useAppDispatch();
  const buildStatus = useAppSelector(selectBuildStatus);
  const progressMessage = useAppSelector(selectProgressMessage);
  const documentTitle = useAppSelector(selectDocumentTitle);
  const documentSummary = useAppSelector(selectDocumentSummary);
  const draftTemplate = useAppSelector(selectDraftTemplate);
  const needsStatusRefetch = useAppSelector(selectNeedsStatusRefetch);

  // Track when polling started for timeout
  const pollingStartTimeRef = useRef<number | null>(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Reset timeout tracking when taskId changes
  useEffect(() => {
    if (taskId) {
      pollingStartTimeRef.current = Date.now();
      setHasTimedOut(false);
    } else {
      pollingStartTimeRef.current = null;
      setHasTimedOut(false);
    }
  }, [taskId]);

  // Check if we've exceeded the timeout
  useEffect(() => {
    // Don't set up timeout if already timed out, no taskId, or in terminal state
    if (!pollingStartTimeRef.current || hasTimedOut || !taskId) return;
    if (buildStatus === 'completed' || buildStatus === 'failed' || buildStatus === 'ready_for_review') {
      return;
    }

    const checkTimeout = () => {
      // Double-check conditions before setting timeout
      if (!pollingStartTimeRef.current || hasTimedOut) return;
      
      const elapsed = Date.now() - pollingStartTimeRef.current;
      if (elapsed >= MAX_POLLING_DURATION_MS) {
        setHasTimedOut(true);
        dispatch(setError('Template build is taking longer than expected. Please try again or contact support.'));
        dispatch(setBuildStatus('failed'));
        // Clear the polling start time to prevent further checks
        pollingStartTimeRef.current = null;
      }
    };

    const interval = setInterval(checkTimeout, 1000); // Check every second
    return () => clearInterval(interval);
  }, [dispatch, hasTimedOut, taskId, buildStatus]);

  // Determine if we should be polling
  // Stop polling if:
  // - No taskId
  // - Status is completed, failed, or ready_for_review (terminal states)
  // - We've timed out
  // - Status is idle or uploading (not yet processing)
  const shouldPoll =
    taskId !== null &&
    !hasTimedOut &&
    buildStatus !== 'completed' &&
    buildStatus !== 'failed' &&
    buildStatus !== 'ready_for_review' &&
    buildStatus !== 'idle' &&
    buildStatus !== 'uploading' &&
    (buildStatus === 'processing' || buildStatus === 'structure_ready');

  // Query with polling.
  // Skip only where there is nothing left to ask for: no task, timed out, or a
  // terminal state. 'ready_for_review' is NOT terminal — it is the state the
  // chat edits in, so the query has to stay subscribed for the post-run
  // reconcile below to have anything to refetch (GDN-3099). Polling is held off
  // separately by `shouldPoll`, so staying subscribed costs one request.
  const shouldSkipQuery = !taskId || hasTimedOut || buildStatus === 'completed' || buildStatus === 'failed';

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetTemplateBuildStatusQuery(
    { taskId: taskId || '' },
    {
      skip: shouldSkipQuery,
      pollingInterval: shouldPoll ? POLL_INTERVAL : 0,
    },
  );

  // The last status payload we copied into Redux. This effect also depends on
  // the Redux values it writes, so without this it re-runs on its own output
  // and re-applies a cached draft over the newer one the chat stream just
  // pushed — the edit visibly reverts (GDN-3099).
  const syncedDataRef = useRef<typeof data>(undefined);

  // Sync API response to Redux state
  useEffect(() => {
    if (!data) return;

    // Don't process data if we've already timed out
    if (hasTimedOut) return;

    // Apply each payload once. RTK Query's structural sharing keeps the same
    // reference when a refetch returns identical data, so this also skips
    // no-op refetches.
    if (syncedDataRef.current === data) return;
    syncedDataRef.current = data;

    // Update build status
    const newStatus = toBuildStatus(data.status);
    if (newStatus !== buildStatus) {
      dispatch(setBuildStatus(newStatus));
      
      // Stop polling if we've reached a terminal state
      if (newStatus === 'completed' || newStatus === 'failed' || newStatus === 'ready_for_review') {
        pollingStartTimeRef.current = null;
        setHasTimedOut(false);
      }
    }

    // Handle API errors
    if (data.error) {
      dispatch(setError(data.error));
      dispatch(setBuildStatus('failed'));
      pollingStartTimeRef.current = null;
      setHasTimedOut(false);
    }

    // Update progress message
    if (data.progress_message !== progressMessage) {
      dispatch(setProgressMessage(data.progress_message ?? null));
    }

    // Update document metadata (for PDFs, available early)
    // Type assertion needed because generated types may not have these fields yet
    const apiData = data as typeof data & {
      document_title?: string | null;
      document_summary?: string | null;
    };

    if (apiData.document_title !== undefined && apiData.document_title !== documentTitle) {
      dispatch(setDocumentTitle(apiData.document_title ?? null));
    }

    if (apiData.document_summary !== undefined && apiData.document_summary !== documentSummary) {
      dispatch(setDocumentSummary(apiData.document_summary ?? null));
    }

    // Which template seeded this task. Needed to tell a save that updated in
    // place from one that forked into a new copy (GDN-3099). Dispatched
    // unconditionally — the apply-once guard above already stops repeats.
    dispatch(setSourceTemplateId(data.source_template_id ?? null));

    // Update draft template if present
    if (data.draft_template) {
      const template = toDraftTemplate(data.draft_template);
      dispatch(setDraftTemplate(template));
    }

    // Set created template ID if build is completed
    if (data.created_template_id) {
      dispatch(setCreatedTemplateId(data.created_template_id));
    }
  }, [data, dispatch, buildStatus, progressMessage, documentTitle, documentSummary, hasTimedOut]);

  // Handle query errors
  useEffect(() => {
    if (error && !hasTimedOut) {
      dispatch(setError('Failed to check template build status. Please try again.'));
      dispatch(setBuildStatus('failed'));
      pollingStartTimeRef.current = null;
      setHasTimedOut(false);
    }
  }, [error, dispatch, hasTimedOut]);

  // Reconcile against Postgres once a chat run finishes (SSE RUN_FINISHED sets
  // the flag). Clear the flag even when we can't act on it, so a stale request
  // can't fire later against a different task.
  useEffect(() => {
    if (!needsStatusRefetch) return;

    if (taskId && !shouldSkipQuery) {
      console.debug('📡 [Status] Triggering refetch after chat completion');
      void refetch();
    }
    dispatch(setNeedsStatusRefetch(false));
  }, [needsStatusRefetch, taskId, shouldSkipQuery, refetch, dispatch]);

  return {
    buildStatus,
    progressMessage,
    documentTitle,
    documentSummary,
    draftTemplate,
    /** True on initial load only (no cached data yet) */
    isLoading,
    /** True during any fetch (initial or polling) */
    isFetching,
    /** True when polling is active (status is still processing) - stable during poll cycles */
    isPolling: shouldPoll,
    error,
    refetch,
    /** Whether the build is ready for chat interaction */
    isReadyForChat: taskId !== null && isReadyForReview(buildStatus),
  };
};

